import { DATA_SOURCES } from '@/config/constants';
import { matchPath, useLocation } from 'react-router';
import { mixpanelTrack, withMixpanelClick } from 'utils/app';
import { HashLink } from 'react-router-hash-link';
import { forwardRef } from 'react';
import { getBreadCrumbs } from 'utils/formatters';
import { getHierarchy } from 'utils/hooks/useGetHierarchy';
import useCountryParam from 'utils/hooks/useCountryParam';

const Link = forwardRef(function Link(
    {
        active,
        to,
        onClick,
        mixpanelContext = 'LINK',
        mixpanelMeta,
        smooth = true,
        ...props
    },
    ref,
) {
    const location = useLocation();
    const { mutateToWithCountryParam } = useCountryParam();

    return (
        <HashLink
            ref={ref}
            to={mutateToWithCountryParam(to)}
            smooth={smooth}
            onClick={(e) => {
                if (onClick) onClick(e);
                if (e.defaultPrevented) return;

                trackNavigation(
                    location.pathname,
                    to,
                    mixpanelContext,
                    mixpanelMeta,
                );
            }}
            {...props}
        />
    );
});

// This function replicates how BreadCrumbs are generated in the BreadCrumbs component
// ToDo: This kind of tracking should probably move to the router and could possibly
// be combined or eliminated by eventual human readable routes
export async function trackNavigation(from, to, context, mixpanelMeta) {
    const initialSelectors = [from, to].map((path) => {
        if (['/industries', '/industries/'].includes(path)) {
            return null;
        }
        const matches = matchPath(path, {
            path: '/:catName(category(Two|Three|Four|Five))/:categoryId',
            exact: true,
        });

        if (!matches) {
            return matches;
        }

        const { params } = matches;

        // TODO(2019-09-02): correct once brand page uses UUID
        if (params.catName && params.catName === 'brands') {
            return null;
        }

        const categoryId = params.categoryId.replace(/(\?.*)|(#.*)/, '');
        const initialSelector = {
            [params.catName === 'industries' ? 'name' : 'id']: categoryId,
        };
        return initialSelector;
    });

    const [fromCat, toCat] = await Promise.all(
        initialSelectors.map(async (initialSelector) => {
            if (initialSelector) {
                const pageBreadCrumbs = await getBreadCrumbs(
                    initialSelector,
                    DATA_SOURCES.POPULARITY,
                    getHierarchy,
                );
                return pageBreadCrumbs
                    .map(({ name }) => name.toUpperCase())
                    .join('-');
            }
            return '';
        }),
    );

    mixpanelTrack('navigation', {
        from,
        fromCat,
        to,
        toCat,
        ...mixpanelMeta,
        context,
    });
}

export const LinkTracked = withMixpanelClick(Link, 'LINK');

export default Link;
